/**
 * Low bandwidth mode ("MrGreenMode", 2026-10-19) — the one browser-side switch.
 *
 * A trader on a thin or lossy link (Medellín, SNF 10-18: 212 KB/s at 2.5 %
 * retransmit against a game tab's ~675 KB/s) cannot keep up with the full game
 * feed. The pref lives server-side (`users.prefs.lowBandwidth`, see
 * `src/lib/traderPrefs.ts`) and reaches this module through `authStore`.
 * Three consumers read it from here and nowhere else:
 *
 *   - the feed gateway scopes (`src/hooks/feedGateway.ts`): `gatewayTabOpts()`
 *     rides beside `scopes` as `opts` — core rows only, OP without player props,
 *     Polymarket alt strikes held until named;
 *   - the push stream (`src/services/relayPush.ts`): `?lowBandwidth=1`;
 *   - book tiers (`src/stores/marketStore.ts`): neighbours and cells on the
 *     coalesced tier.
 *
 * "Show for this game": a props section that the trader opens explicitly holds
 * `acquireFullFeed()` while it is open; the tab's opts widen back to the whole
 * game (rows + OP props) without leaving the mode.
 */
import { useSyncExternalStore } from 'react';
import { useAuthStore } from './authStore';

/** The gateway's per-tab options (relay `parseTabOpts`). Absent facet = full. */
export interface GatewayTabOpts {
  opProps?: boolean;
  rows?: 'all' | 'core';
  gameHold?: 'off' | 'poly' | 'all';
}

const LOW_BW_OPTS: Readonly<GatewayTabOpts> = Object.freeze({ opProps: false, rows: 'core', gameHold: 'poly' });
const WIDENED_OPTS: Readonly<GatewayTabOpts> = Object.freeze({ opProps: true, rows: 'all', gameHold: 'poly' });

const listeners = new Set<() => void>();
let fullFeedHolds = 0;
let lastPref = false;
let wired = false;

function prefOn(): boolean {
  return useAuthStore.getState().user?.prefs?.lowBandwidth === true;
}

function emit(): void {
  for (const fn of listeners) fn();
}

/** Follows the pref from first use, not at import: the push client and the
 *  status bar import this module, and a test that mocks `authStore` without a
 *  `subscribe` must still load them (it simply reads "off"). */
function wire(): void {
  if (wired) return;
  wired = true;
  const store = useAuthStore as Partial<typeof useAuthStore> | undefined;
  if (typeof store?.getState !== 'function' || typeof store.subscribe !== 'function') return;
  lastPref = prefOn();
  store.subscribe(() => {
    const v = prefOn();
    if (v === lastPref) return;
    lastPref = v;
    emit();
  });
}

/** True when the signed-in trader has low bandwidth mode on. */
export function lowBandwidthEnabled(): boolean {
  wire();
  return lastPref;
}

/** True while some opened section holds the whole game ("show for this game"). */
export function fullFeedHeld(): boolean {
  return fullFeedHolds > 0;
}

/** The gateway `opts` for this tab: undefined when the mode is off (the hub's
 *  full default — an old hub and an old tab behave exactly as before). */
export function gatewayTabOpts(): GatewayTabOpts | undefined {
  wire();
  if (!lastPref) return undefined;
  return fullFeedHolds > 0 ? WIDENED_OPTS : LOW_BW_OPTS;
}

/** Widens the tab back to the whole game while held. Returns the release
 *  (idempotent). Holds are counted; the opts change on 0 ↔ 1 only. */
export function acquireFullFeed(): () => void {
  fullFeedHolds += 1;
  if (fullFeedHolds === 1) emit();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    fullFeedHolds = Math.max(0, fullFeedHolds - 1);
    if (fullFeedHolds === 0) emit();
  };
}

/** Fires when the mode or the full-feed hold changes the tab's opts. */
export function subscribeLowBandwidth(fn: () => void): () => void {
  wire();
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** React: is low bandwidth mode on for this trader? */
export function useLowBandwidth(): boolean {
  return useSyncExternalStore(subscribeLowBandwidth, lowBandwidthEnabled, () => false);
}

/** React: is some section holding the whole game right now? */
export function useFullFeedHeld(): boolean {
  return useSyncExternalStore(subscribeLowBandwidth, fullFeedHeld, () => false);
}

/** Tests only: drop holds and re-read the pref. */
export function __resetLowBandwidthForTests(): void {
  fullFeedHolds = 0;
  if (wired) lastPref = prefOn();
}
